import React from "react";
import { NextSeo } from "next-seo";
import { Nav } from "@/components/navigation/Navbar";
import Crumbs from "@/components/navigation/Crumbs";
import * as Text from "@/components/text";
import qs from "qs";
import apiClient from "@/utils/apiClient";
import Book from "@/components/initiatives/Book";
import Magazine from "@/components/initiatives/Magazine";
import HeroMedia from "@/components/lib/HeroMedia";
import { localeTexts } from "@/utils/locale";

export default function Editions({ mediaCenter, locale }) {
  // We build a list of all available localizations from the prop.
  const allVersions = [
    { attributes: mediaCenter },
    ...(mediaCenter.localizations?.data || []),
  ];

  // Find the page data that matches the currently selected `locale`.
  const currentPageData = allVersions.find(
    (v) => v.attributes.locale === locale
  )?.attributes;

  // Fallback to the initial prop if the correct localization isn't found.
  const basePageData = currentPageData || mediaCenter;

  // The Spanish ("es-MX") version is the source of truth for the list of books.
  const spanishData = allVersions.find(
    (v) => v.attributes.locale === "es-MX"
  )?.attributes;

  let finalBooks = spanishData?.libros?.data || [];

  // If the current locale is English, translate the books from the Spanish list.
  if (locale === "en" && spanishData?.libros?.data) {
    finalBooks = spanishData.libros.data.map((book) => {
      const englishBookLocalization =
        book.attributes.localizations?.data?.find(
          (localization) => localization.attributes.locale === "en"
        );
      if (englishBookLocalization) {
        return {
          id: englishBookLocalization.id,
          attributes: {
            ...book.attributes,
            ...englishBookLocalization.attributes,
          },
        };
      }
      return book; // Fallback to the Spanish book if no translation exists.
    });
  }

  // Assemble the final data object for rendering.
  const mediaCenterData = {
    ...basePageData,
    libros: {
      data: finalBooks,
    },
    revistas: spanishData?.revistas,
  };
  return (
    <>
      <div className="amb-container">
        <Nav className="bg-gray-100 grow" theme="light" />
        <NextSeo
          title="Ambulante Ediciones - Ambulante"
          description="Información general Coordinación de comunicación Sofía Llorente / slz@ambulante.org Coordinación de prensa Icunacury Acosta / prensa@ambulante.org Boletines 2023 Ambulante Gira de Documentales revela la imagen de su decimoctava edición Anunciamos el arranque de Ambulante Presenta 2023 Materiales Ambulante Presenta 2023 Catálogo Ambulante Presenta 2023 Guía de discusión Las colonias Programas de mano ediciones pasadas Ambulante"
          canonical={`https://ambulante.org/ambulante-ediciones}`}
          openGraph={{
            url: `https://ambulante.org/ambulante-ediciones`,
            title: "Ambulante Ediciones - Ambulante",
            description:
              "Información general Coordinación de comunicación Sofía Llorente / slz@ambulante.org Coordinación de prensa Icunacury Acosta / prensa@ambulante.org Boletines 2023 Ambulante Gira de Documentales revela la imagen de su decimoctava edición Anunciamos el arranque de Ambulante Presenta 2023 Materiales Ambulante Presenta 2023 Catálogo Ambulante Presenta 2023 Guía de discusión Las colonias Programas de mano ediciones pasadas Ambulante",
            site_name: "Ambulante",
          }}
        />
        <div className="flex flex-row gap-2 py-4">
          <Crumbs
            breadcrumbs={[
              {
                href: "/",
                text:
                  localeTexts[locale].breadcrumbs.home ||
                  localeTexts[locale].breadcrumbs.Home,
              },
              {
                href: "/iniciativas/gira?seccion=las-peliculas",
                text: localeTexts[locale].breadcrumbs.programs,
              },
            ]}
          />
          <Text.NavigationLabel className="mt-0.5 text-xs font-thin text-neutral-600 md:mt-1.5">
            {localeTexts[locale].AmbulanteEdiciones.navigationEdiciones}
          </Text.NavigationLabel>
        </div>
        <div className="flex flex-col items-center justify-center mb-8 text-center mt-7 md:mt-10 md:px-28 lg:mt-14 lg:px-32">
          <div className="w-full font-bold text-center text-ediciones lg:mb-1">
            <Text.Title className="text-[22px] md:text-2xl md:font-semibold lg:text-2xl">
              {localeTexts[locale].AmbulanteEdiciones.title}
            </Text.Title>
          </div>
          <div className="w-full max-w-[650px] px-1 py-2 text-center text-4xl font-bold leading-9 md:my-2 md:text-[30px] md:leading-[32px] lg:mb-0 lg:text-4xl">
            {mediaCenterData?.title}
          </div>
          <div className="w-full max-w-[750px] text-center text-xl font-normal leading-7 lg:mt-4 lg:text-[30px] lg:leading-[42px]">
            {mediaCenterData?.subtitle}
          </div>
          {mediaCenterData?.description ? (
            <div
              className={`w-full max-w-[950px] text-center text-sm font-light leading-7 lg:mt-4 lg:text-[18px] lg:leading-[22px] bg-[#f1f6fb] p-4 rounded-md`}
            >
              {mediaCenterData?.description}
            </div>
          ) : null}
        </div>
        {mediaCenterData?.hero_media && (
          <div className="flex items-center justify-center w-full">
            <div className="w-full pb-8 lg:max-w-[1440px] lg:pt-12">
              <HeroMedia media={mediaCenterData?.hero_media} />
            </div>
          </div>
        )}
        <div className="py-4 amb-ambulante md:mb-8">
          <Text.Headline className="text-[40px]">
            {localeTexts[locale].AmbulanteEdiciones.libros}
          </Text.Headline>
        </div>
      </div>
      <Book data={mediaCenterData?.libros?.data} locale={locale} />
      <Magazine data={mediaCenterData?.revistas?.data} locale={locale} />
    </>
  );
}

export async function getServerSideProps(context) {
  const { locale } = context.query;
  const q = qs.stringify({
    populate: {
      localizations: {
        populate: {
          hero_media: "*",
          libros: {
            populate: {
              images: "*",
              localizations: "*",
            },
          },
          revistas: {
            populate: {
              images: "*",
              localizations: "*",
            },
          },
        },
      },
      hero_media: "*",
      libros: {
        populate: {
          images: "*",
          localizations: "*",
        },
      },
      revistas: {
        populate: {
          images: "*",
          localizations: "*",
        },
      },
    },
  });
  try {
    const { data } = await apiClient.get(
      `/api/ambulante-edicion?locale=${locale || "es-MX"}&${q}`
    );
    return {
      props: {
        locale: locale || "es-MX",
        mediaCenter: data.data.attributes,
      },
    };
  } catch (err) {
    console.log(err);
    return {
      notFound: true,
    };
  }
}
